import { api } from "./api";
import { h, replace } from "./dom";
import { icon } from "./icons";
import { store } from "./state";
import type { AlbumSummary, ManagedShare } from "./types";
import { toast } from "./ui";

const statusText = (share: ManagedShare): string => {
  if (share.deliveryStatus === "failed") return "Email failed";
  if (share.status === "pending_subscription") return "Subscription check pending";
  if (share.status === "invited") return "Invitation sent";
  return share.permission === "edit" ? "Can edit now" : "Can view now";
};

export async function openSharing(): Promise<void> {
  const dialog = h("dialog", { class: "modal share-modal" });
  const body = h("div", { class: "modal-body" }, h("div", { class: "loading", text: "Loading sharing…" }));
  dialog.appendChild(body);
  const close = (): void => {
    dialog.close();
    dialog.addEventListener("close", () => dialog.remove(), { once: true });
    if (!dialog.open) dialog.remove();
  };
  dialog.addEventListener("cancel", (event) => {
    event.preventDefault();
    close();
  });
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) close();
  });
  document.body.appendChild(dialog);
  dialog.showModal();

  let albums: AlbumSummary[] = [];
  let shares: ManagedShare[] = [];
  try {
    [albums, shares] = await Promise.all([api.albums(store.libraryId), api.shares(store.libraryId)]);
  } catch (error) {
    replace(body,
      h("h2", { class: "modal-title", text: "Sharing" }),
      h("p", { class: "modal-say", text: error instanceof Error ? error.message : "Could not load sharing." }),
      h("div", { class: "modal-actions" }, h("button", { class: "btn ghost", onclick: close }, "Close")),
    );
    return;
  }

  const render = (): void => {
    const email = h("input", {
      type: "email",
      name: "email",
      placeholder: "relative@example.com",
      autocomplete: "email",
      required: true,
    });
    const scope = h(
      "select",
      { name: "scope" },
      h("option", { value: "archive", text: "Entire archive" }),
      h("option", { value: "album", text: "One album" }),
    );
    const album = h(
      "select",
      { name: "album" },
      ...albums.map((entry) => h("option", {
        value: entry.id,
        text: entry.name,
        selected: entry.id === store.filter.albumId,
      })),
    );
    const albumField = h(
      "label",
      { class: "modal-field", hidden: "" },
      h("span", { class: "modal-label", text: "Album" }),
      h("span", { class: "modal-select-wrap" }, album, icon("chevron", "i sm")),
    );
    const permission = h(
      "select",
      { name: "permission" },
      h("option", { value: "view", text: "Can view" }),
      h("option", { value: "edit", text: "Can edit" }),
    );
    const subscription = h("div", { class: "share-entitlement" },
      icon("info", "i sm"),
      h("span", { text: "Subscriber eligibility check is not connected yet. Access that needs a subscriber is held pending." }),
    );
    const submit = h("button", { class: "btn primary", type: "submit", text: "Send invitation" });
    scope.addEventListener("change", () => {
      const albumScope = scope.value === "album";
      albumField.toggleAttribute("hidden", !albumScope);
      if (albumScope && !albums.length) submit.setAttribute("disabled", "");
      else submit.removeAttribute("disabled");
    });

    const form = h(
      "form",
      { class: "share-form" },
      h("label", { class: "modal-field" }, h("span", { class: "modal-label", text: "Invite by email" }), email),
      h("div", { class: "share-fields" },
        h("label", { class: "modal-field" },
          h("span", { class: "modal-label", text: "Share" }),
          h("span", { class: "modal-select-wrap" }, scope, icon("chevron", "i sm")),
        ),
        h("label", { class: "modal-field" },
          h("span", { class: "modal-label", text: "Permission" }),
          h("span", { class: "modal-select-wrap" }, permission, icon("chevron", "i sm")),
        ),
      ),
      albumField,
      subscription,
      h("div", { class: "share-submit" }, submit),
    );
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const address = email.value.trim();
      if (!address) return email.focus();
      submit.setAttribute("disabled", "");
      submit.textContent = "Sending…";
      void api.inviteShare(store.libraryId, {
        email: address,
        scope: scope.value as "album" | "archive",
        permission: permission.value as "view" | "edit",
        ...(scope.value === "album" ? { albumId: album.value } : {}),
      }).then((created) => {
        shares = [created, ...shares];
        toast(created.deliveryStatus === "failed" ? "Invitation saved, but the email could not be sent." : "Invitation sent.", created.deliveryStatus === "failed");
        render();
      }).catch((error: unknown) => {
        submit.removeAttribute("disabled");
        submit.textContent = "Send invitation";
        toast(error instanceof Error ? error.message : "Could not send the invitation.", true);
      });
    });

    const rows = shares.length
      ? shares.map((share) => h(
          "div",
          { class: "share-row" },
          h("span", { class: "share-avatar", text: share.email.slice(0, 1).toUpperCase() }),
          h("span", { class: "share-copy" },
            h("strong", { text: share.email }),
            h("small", { text: `${share.albumName ?? "Entire archive"} · Can ${share.permission}` }),
          ),
          h("span", { class: `share-status ${share.status}`, text: statusText(share) }),
          h("button", {
            class: "ib share-remove",
            type: "button",
            title: `Remove ${share.email}`,
            "aria-label": `Remove sharing for ${share.email}`,
            onclick: () => {
              void api.revokeShare(store.libraryId, share.id).then(() => {
                shares = shares.filter((entry) => entry.id !== share.id);
                render();
                toast("Access removed.");
              }).catch((error: unknown) => toast(error instanceof Error ? error.message : "Could not remove access.", true));
            },
          }, icon("x")),
        ))
      : [h("p", { class: "share-empty", text: "Nobody else has access yet." })];

    replace(body,
      h("div", { class: "share-head" },
        h("div", null,
          h("h2", { class: "modal-title", text: "Share this archive" }),
          h("p", { class: "modal-say", text: store.stats?.library.name ?? "Invite people and choose what they can do." }),
        ),
        h("button", { class: "ib", title: "Close", "aria-label": "Close", onclick: close }, icon("x")),
      ),
      form,
      h("div", { class: "share-list-head" }, h("span", { text: "People with access" }), h("span", { text: String(shares.length) })),
      h("div", { class: "share-list" }, ...rows),
    );
    email.focus();
  };

  render();
}
